import { coursePackageApi } from '@/api/course-package.api';
import {
  CoursePackageResponseDTO,
  UpsertCoursePackageApiDTO
} from '@/jbi-shared/dto/jtms.dtos';
import { RootState } from '@/store/store';
import { DispatchOptions } from 'vuex';
import { IndexBooleanPayload } from '../course-module/course-module.types';
import {
  CoursePackageComponent,
  CoursePackageState,
  defaultCoursePackage,
  UpdateCoursePackageStatePayload
} from './course-package.types';

interface CoursePackageActionContext {
  commit: (mutation: string, payload?: any) => void;
  dispatch: (
    action: string,
    payload?: any,
    options?: DispatchOptions
  ) => Promise<any>;
  state: CoursePackageState;
  rootState: RootState;
}

export const coursePackageActions = {
  async upsertCoursePackages(
    { commit }: CoursePackageActionContext,
    payload: UpsertCoursePackageApiDTO
  ) {
    try {
      commit('UPSERT_COURSE_PACKAGE_LOADING');

      const { data } = await coursePackageApi.upsertCoursePackageApi(payload);

      commit('UPSERT_COURSE_PACKAGE_SUCCESS', data);
    } catch (error) {
      commit('UPSERT_COURSE_PACKAGE_ERROR', error);
      throw error;
    }
  },

  async deleteCoursePackages(
    { commit, dispatch, state, rootState }: CoursePackageActionContext,
    payload: number[]
  ) {
    try {
      commit('DELETE_COURSE_PACKAGE_LOADING');

      const { data } = await coursePackageApi.deleteCoursePackageApi(payload);

      if (data) {
        // Search for deleted item in active array
        const itemToRemoveIndex = state.coursePackageTabState.activeCoursePackages.findIndex(
          (pkg) => {
            if (data.length > 0) {
              return pkg.id === data[0].id;
            }

            return -1;
          }
        );

        if (itemToRemoveIndex > -1) {
          dispatch('removeActiveCoursePackage', itemToRemoveIndex);
        }

        // Only re-add default course package if no course modules are found after deletion
        if (
          rootState.courseModule.courseModulesTabState.activeCourseModules
            .length === 0
        ) {
          dispatch('clearActiveCoursePackages');
          dispatch('addActiveCoursePackage', defaultCoursePackage());
        }

        commit('DELETE_COURSE_PACKAGE_SUCCESS');
      }
    } catch (error) {
      commit('DELETE_COURSE_PACKAGE_ERROR', error);
      throw error;
    }
  },

  /**
   * Clears the list of active course packages. Revalidates all fields.
   */
  clearActiveCoursePackages({ commit, dispatch }: CoursePackageActionContext) {
    commit('CLEAR_ACTIVE_COURSES_PACKAGES');
    dispatch('validateAllCoursePackages');
  },

  /**
   * Adds a new course package to the active list. Automatically updates the label
   * and revalidates all fields.
   * @param payload - The course package item to add.
   */
  addActiveCoursePackage(
    { commit, dispatch }: CoursePackageActionContext,
    payload: CoursePackageComponent
  ) {
    commit('ADD_ACTIVE_COURSE_PACKAGE', payload);
    dispatch('validateCourseModuleDropdownState');
    dispatch('updateCoursePackageLabel');
    dispatch('recalculateState');
  },

  /**
   * Updates a specific course package in the active list by its index with new data.
   * @param payload - An object containing the index and the partial data to update.
   */
  updateCoursePackage(
    { commit, dispatch, rootState }: CoursePackageActionContext,
    payload: UpdateCoursePackageStatePayload
  ) {
    commit('UPDATE_COURSE_PACKAGE', payload);
    dispatch('validateCourseModuleDropdownState');

    // Only check for duplicates if course modules are present
    if (rootState.course.courseCreationState.hasModules) {
      dispatch('validateDuplicateCoursePackages');
    }

    dispatch('recalculateState');
  },

  /**
   * Removes a course package from the active list by its index.
   * @param index - The index of the course package to remove.
   */
  removeActiveCoursePackage(
    { commit, dispatch, rootState }: CoursePackageActionContext,
    index: number
  ) {
    commit('REMOVE_ACTIVE_COURSE_PACKAGE', index);
    dispatch('updateCoursePackageLabel');

    // Only check for duplicates if course modules are present
    if (rootState.course.courseCreationState.hasModules) {
      dispatch('validateDuplicateCoursePackages');
    }
    dispatch('recalculateState');
  },

  /**
   * Course package labels are dynamically assigned upon creation, based on index.
   * This action ensures correct labels are reassigned when course packages are
   * deleted/added.
   */
  updateCoursePackageLabel({ commit }: CoursePackageActionContext) {
    commit('UPDATE_COURSE_PACKAGE_LABEL');
  },

  /**
   * Updates the validation error status for a single course package.
   * @param payload - An object containing the index and the boolean error status.
   */
  updateCoursePackageFieldError(
    { commit, dispatch }: CoursePackageActionContext,
    payload: IndexBooleanPayload
  ) {
    commit('UPDATE_COURSE_PACKAGE_FIELD_ERROR', payload);
    dispatch('validateAllCoursePackages');
  },

  /**
   * Manully update ALL the course module dropdown selection for all course packages.
   * @param shouldDisable - A boolean indicating if the course object contains valida course modules.
   */
  updateAllCoursePackagesCourseModuleDropdown(
    { commit }: CoursePackageActionContext,
    shouldDisable: boolean
  ) {
    commit('UPDATE_ALL_COURSE_PACKAGES_COURSE_MODULE_DROPDOWN', shouldDisable);
  },

  /**
   * Assigns a list of existing course modules, converting them to the component's state format.
   * This action clears the existing active list before adding the new ones.
   * @param payload - An array of course modules from an API response.
   */
  assignExistingCoursePackages(
    { commit, dispatch }: CoursePackageActionContext,
    payload: CoursePackageResponseDTO[]
  ) {
    commit('ASSIGN_EXISTING_COURSE_PACKAGES', payload);
    dispatch('recalculateState');
  },

  // REMOVE_COURSE_MODULE_REFERENCES
  removeCourseModuleReferences(
    { commit }: CoursePackageActionContext,
    courseModuleIdsToDelete: number
  ) {
    commit('REMOVE_COURSE_MODULE_REFERENCES', courseModuleIdsToDelete);
  },

  /**
   * Validates and updates the disabled state of the course module dropdown for all packages.
   */
  validateCourseModuleDropdownState({
    commit,
    rootState
  }: CoursePackageActionContext) {
    // Get the active course modules from the other store module
    const allActiveCourseModules =
      rootState.courseModule.courseModulesTabState.activeCourseModules;
    commit('VALIDATE_COURSE_MODULE_DROPDOWN_STATE', allActiveCourseModules);
  },

  validateDuplicateCoursePackages({ commit }: CoursePackageActionContext) {
    commit('VALIDATE_DUPLICATE_COURSE_PACKAGES');
  },

  /**
   * Triggers a validation check across all active course packages to determine
   * the overall form error state.
   */
  validateAllCoursePackages({ commit, rootState }: CoursePackageActionContext) {
    const hasCourseModules: boolean =
      rootState.course.courseCreationState.hasModules;
    const isFreeCourse: boolean = rootState.course.courseCreationState
      .isFreeCourse!;

    commit('VALIDATE_ALL_COURSE_PACKAGES', {
      hasModules: hasCourseModules,
      isFreeCourse
    });
  },

  /**
   * Toggles display for the currency and amount fields in CoursePackageComponent
   * depending on free course
   */
  toggleCurrencyAndAmountFields(
    { commit }: CoursePackageActionContext,
    shouldDisable: boolean
  ) {
    commit('TOGGLE_CURRENCY_AND_AMOUNT_FIELDS', shouldDisable);
  },

  /**
   * Manual trigger to re-calculate the states across all packages based on the changes.
   */
  recalculateState({ dispatch }: CoursePackageActionContext) {
    dispatch('validateAllCoursePackages');
    dispatch('validateCourseModuleDropdownState');
    dispatch('validateDuplicateCoursePackages');
  }
};
